import {isEmpty, isNil} from 'ramda';
import {DateTime} from 'luxon';
import {v4 as uuidv4} from 'uuid';
import {isNationalIdentificationNumberValid} from 'taiwan-id-validator';

class User implements User {
  [key: string]: any;
  id: string;
  code?: string;
  firstName?: string;
  lastName?: string;
  gender?: number;
  nationality?: string;
  certificateType?: number;
  certificateNo?: string;
  birthday?: string;
  onboardingDate?: string;
  phoneNo?: string;
  address?: string;
  contactAddress?: string;
  staffType?: number;
  jobTitle?: string;
  errors?: {[key: string]: string | null};
  seqNo: string | undefined;
  careerSeniority?: number; // 工作年資
  careerRecognizedSeniority?: number; // 專業年資
  jobGrade?: string; // 職等
  jobLevel?: string; // 職級
  businessEmail?: string; // 公司信箱
  englishName?: string; // 英文姓名
  maritalStatus?: number; // 婚姻狀態(1:已婚; 2:未婚; 允許null)
  landlineNumber?: string; // 住家電話
  extensionNumber?: string; // 分機
  businessMobileNumber?: string; // 公務手機
  personalEmail?: string; // 私人信箱
  emergency?: string; // 緊急聯絡人
  emergencyRelationship?: string; // 緊急聯絡人關係
  emergencyMobileNumber?: string; // 緊急聯絡人電話(手機)

  constructor(
    data: Omit<User, 'errors' | 'validate' | 'seqNo'> & {seqNo?: string},
  ) {
    this.id = data.id ? data.id : uuidv4();
    this.code = data.code?.trim();
    this.seqNo = data.seqNo
    this.firstName = data.firstName?.trim();
    this.lastName = data.lastName?.trim();
    this.gender = !isNil(data.gender) ? Number(data.gender) : undefined;
    this.nationality = data.nationality || 'TW';
    this.certificateType = !isNil(data.certificateType)
      ? Number(data.certificateType)
      : 1;
    this.certificateNo = !isNil(data.certificateNo)
      ? data.certificateNo.trim()
      : undefined;
    this.birthday = data.birthday?.trim();
    this.onboardingDate = data.onboardingDate?.trim();
    this.phoneNo = data.phoneNo;
    this.address = data.address?.trim();
    this.contactAddress = data.contactAddress?.trim();
    this.staffType = !isNil(data.staffType) ? Number(data.staffType) : 2;
    this.jobTitle = data.jobTitle;
    this.careerSeniority = !isNil(data.careerSeniority) ? data.careerSeniority : 0; // 工作年資
    this.careerRecognizedSeniority = !isNil(data.careerRecognizedSeniority) ? data.careerRecognizedSeniority : 0; // 專業年資
    this.jobGrade = data.jobGrade?.trim(); // 職等
    this.jobLevel = data.jobLevel?.trim(); // 職級
    this.businessEmail = data.businessEmail?.trim(); // 私人信箱
    this.englishName = data.englishName?.trim(); // 英文姓名
    this.maritalStatus = !isNil(data.maritalStatus) ? Number(data.maritalStatus) : undefined;
    this.landlineNumber = data.landlineNumber; // 住家電話
    this.extensionNumber = data.extensionNumber; // 分機
    this.businessMobileNumber = data.businessMobileNumber; // 公務手機
    this.personalEmail = data.personalEmail?.trim(); // 私人信箱
    this.emergency = data.emergency?.trim(); // 緊急聯絡人
    this.emergencyRelationship = data.emergencyRelationship?.trim(); // 緊急聯絡人關係
    this.emergencyMobileNumber = data.emergencyMobileNumber; // 緊急聯絡人電話(手機)
    this.errors = this.validate();
  }

  private requireMessage(varName: string) {
    return `${varName} 為必填`;
  }

  public static validateDuplicate(users: User[]) {
    const employeeNumbers = new Map<string, User>();
    const idNumbers = new Map<string, User>();
    const businessEmail = new Map<string, User>();
    users.forEach(user => {
      const employeeNumber = `${user.code}${user.seqNo}`;
      const employeeNumberErrorMsg = '工號不可重覆';
      const certificateNoErrorMsg = '證件號碼重覆';
      const businessEmailErrorMsg = '公司信箱不可重覆';

      if (user.code && user.seqNo && employeeNumbers.has(employeeNumber)) {
        const errors = user.errors || {};
        errors['seqNo'] = employeeNumberErrorMsg;
        user.errors = errors;
        const duplicateUser = employeeNumbers.get(employeeNumber);
        if (duplicateUser) {
          const duplicateUserError = duplicateUser.errors || {};
          duplicateUserError['seqNo'] = employeeNumberErrorMsg;
          duplicateUser.errors = duplicateUserError;
        }
      } else if (user.code && user.seqNo) {
        const errors = user.errors || {};
        errors['seqNo'] = errors['seqNo'] === employeeNumberErrorMsg ? null : errors['seqNo'];;
        user.errors = errors;
        employeeNumbers.set(employeeNumber, user);
      }

      if (user.certificateNo && idNumbers.has(user.certificateNo)) {
        const errors = user.errors || {};
        errors['certificateNo'] = certificateNoErrorMsg;
        user.errors = errors;
        const duplicateUser = idNumbers.get(user.certificateNo);
        if (duplicateUser) {
          const duplicateUserError = duplicateUser.errors || {};
          duplicateUserError['certificateNo'] = certificateNoErrorMsg;
          duplicateUser.errors = duplicateUserError;
        }
      } else if (user.certificateNo) {
        const errors = user.errors || {};
        errors['certificateNo'] = errors['certificateNo'] === certificateNoErrorMsg ? null : errors['certificateNo'];
        user.errors = errors;
        idNumbers.set(user.certificateNo, user);
      }

      if(user.businessEmail && businessEmail.has(user.businessEmail)) {
        const errors = user.errors || {};
        errors['businessEmail'] = businessEmailErrorMsg;
        user.errors = errors;
      } else if (user.businessEmail) {
        const errors = user.errors || {};
        errors['businessEmail'] = errors['businessEmail'] === businessEmailErrorMsg ? null : errors['businessEmail'];;
        user.errors = errors;
        businessEmail.set(user.businessEmail, user);
      }

    });
  }

  public validate(): {[key: string]: string | null} {
    const errors = {
      code:
        (isEmpty(this.code) || isNil(this.code)
          ? this.requireMessage('工號前置碼')
          : null) ||
        (this.code && this.code.length > 12
          ? '最多 12 碼'
          : null),

      seqNo:
        isEmpty(this.seqNo) || isNil(this.seqNo)
          ? this.requireMessage('工號流水號')
          : !/^\d{3,8}$/.test(this.seqNo)
          ? '僅限數字(3 - 8碼)'
          : null,
      lastName:
        isEmpty(this.lastName) || isNil(this.lastName)
          ? this.requireMessage('姓')
          : null,
      firstName:
        isEmpty(this.firstName) || isNil(this.firstName)
          ? this.requireMessage('名')
          : null,
      gender:
        isEmpty(this.gender) || isNil(this.gender)
          ? this.requireMessage('性別')
          : null,
      nationality:
        isEmpty(this.nationality) || isNil(this.nationality)
          ? this.requireMessage('國籍')
          : null,
      certificateType:
        isEmpty(this.certificateType) || isNil(this.certificateType)
          ? this.requireMessage('證件類別')
          : null,
      certificateNo:
        isEmpty(this.certificateNo) || isNil(this.certificateNo)
          ? this.requireMessage('證件號碼')
          : !/^[\s\d\w-_+{}!@$%&()#]+$/.test(this.certificateNo)
          ? '證件號碼錯誤'
          : this.certificateType === 1 &&
            !isNationalIdentificationNumberValid(this.certificateNo)
          ? '證件號碼錯誤'
          : null,
      birthday:
        isEmpty(this.birthday) || isNil(this.birthday)
          ? this.requireMessage('生日')
          : DateTime.fromFormat(this.birthday, 'yyyy/MM/dd').isValid
          ? DateTime.fromFormat(this.birthday, 'yyyy/MM/dd') <=
            DateTime.now().startOf('day')
            ? null
            : '請勿填寫未來日期'
          : '正確日期格式：YYYY/MM/DD',
      onboardingDate:
        isEmpty(this.onboardingDate) || isNil(this.onboardingDate)
          ? this.requireMessage('到職日')
          : !DateTime.fromFormat(this.onboardingDate, 'yyyy/MM/dd').isValid
          ? '正確日期格式：YYYY/MM/DD'
          : DateTime.fromFormat(this.onboardingDate, 'yyyy/MM/dd') <=
            DateTime.fromFormat('1973/01/01', 'yyyy/MM/dd')
          ? '到職日不可早於1973/01/02'
          : null,
      phoneNo:
        isEmpty(this.phoneNo) || isNil(this.phoneNo)
          ? null
          : !/^[\d-_+{}!@$%&()# ]+$/.test(this.phoneNo)
          ? '僅限數字與符號'
          : null,
      address:
        isEmpty(this.address) || isNil(this.address)
          ? this.requireMessage('戶籍地址')
          : null,
      contactAddress:
        isEmpty(this.contactAddress) || isNil(this.contactAddress)
          ? this.requireMessage('通訊地址')
          : null,
      staffType:
        isEmpty(this.staffType) || isNil(this.staffType)
          ? this.requireMessage('身份類別')
          : null,
      jobTitle:
        isEmpty(this.jobTitle) || isNil(this.jobTitle)
          ? this.requireMessage('職稱')
          : null,
      careerSeniority:
        isEmpty(this.careerSeniority) || isNil(this.careerSeniority)
          ? this.requireMessage('工作年資')
          : !/^(\b(0+)?\d{1,9}|\b(0+)?\d{1,9}\.)?(:?\.\d{1,1}?|\.\d{1,1}?(0+)\b)?$/.test(String(this.careerSeniority))
          ? '需為數字，限制小數點以下1位'
          : !(isEmpty(this.careerRecognizedSeniority) || isNil(this.careerRecognizedSeniority)) && (Number(this.careerSeniority) < Number(this.careerRecognizedSeniority))
          ? '工作年資應大於等於專業年資'
          : null,
      careerRecognizedSeniority:
        isEmpty(this.careerRecognizedSeniority) || isNil(this.careerRecognizedSeniority)
          ? this.requireMessage('專業年資')
          : !/^(\b(0+)?\d{1,9}|\b(0+)?\d{1,9}\.)?(:?\.\d{1,1}?|\.\d{1,1}?(0+)\b)?$/.test(String(this.careerRecognizedSeniority))
          ? '需為數字，限制小數點以下1位'
          :  !(isEmpty(this.careerSeniority) || isNil(this.careerSeniority)) && (Number(this.careerSeniority) < Number(this.careerRecognizedSeniority))
          ? '工作年資應大於等於專業年資'
          : null,
      landlineNumber:
        isEmpty(this.landlineNumber) || isNil(this.landlineNumber)
          ? null
          : !/^[\d-_+{}!@$%&()# ]+$/.test(this.landlineNumber)
          ? '僅限數字與符號'
          : null,
      extensionNumber:
        isEmpty(this.extensionNumber) || isNil(this.extensionNumber)
          ? null
          : !/^[\d-_+{}!@$%&()# ]+$/.test(this.extensionNumber)
          ? '僅限數字與符號'
          : null,
      businessMobileNumber:
        isEmpty(this.businessMobileNumber) || isNil(this.businessMobileNumber)
          ? null
          : !/^[\d-_+{}!@$%&()# ]+$/.test(this.businessMobileNumber)
          ? '僅限數字與符號'
          : null,
      emergencyMobileNumber:
        isEmpty(this.emergencyMobileNumber) || isNil(this.emergencyMobileNumber)
          ? null
          : !/^[\d-_+{}!@$%&()# ]+$/.test(this.emergencyMobileNumber)
          ? '僅限數字與符號'
          : null,
      personalEmail:
        isEmpty(this.personalEmail) || isNil(this.personalEmail)
          ? null
          : !/^\w+((-\w+)|(\.\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/.test(this.personalEmail)
          ? 'E-mail格式有誤'
          : null,
      businessEmail:
        isEmpty(this.businessEmail) || isNil(this.businessEmail)
          ? null
          : !/^\w+((-\w+)|(\.\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/.test(this.businessEmail)
          ? 'E-mail格式有誤'
          : DateTime.fromFormat(this.onboardingDate || '', 'yyyy/MM/dd') > DateTime.now()
          ? '不可填寫公司信箱，未來到職人員請於到職後至 XE 系統填寫'
          : null,
    };
    this.errors = errors;

    return errors;
  }
}

export default User;
